/**
 * Tables Page - Card Layout
 * Refactored with Clean Architecture pattern
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';

import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Plus, Table2, Edit, Trash2, Users, Loader2 } from 'lucide-react';

import TableModal from './TableModal';
import './styles.css';

const statusConfig = {
  disponivel: { label: 'Disponível', variant: 'default', class: 'table-card-available' },
  ocupada: { label: 'Ocupada', variant: 'destructive', class: 'table-card-occupied' },
  reservada: { label: 'Reservada', variant: 'secondary', class: 'table-card-reserved' },
};

export default function Tables() {
  const { t } = useTranslation();
  
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/tables');
      setTables(res.data);
    } catch (err) {
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      setSaving(true);
      if (editingTable?.id) {
        await api.put(`/tables/${editingTable.id}`, data);
      } else {
        await api.post('/tables', data);
      }
      toast.success(t('tables.saveSuccess'));
      setModalOpen(false);
      setEditingTable(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  // Handle modal close - reset editingTable to null
  const handleModalOpenChange = (open) => {
    setModalOpen(open);
    if (!open) {
      setTimeout(() => setEditingTable(null), 100);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/tables/${deleteId}`);
      toast.success(t('tables.deleteSuccess'));
      setDeleteId(null);
      loadData();
    } catch (err) {
      toast.error(t('errors.generic'));
    }
  };

  const handleStatusChange = async (table, newStatus) => {
    try {
      await api.put(`/tables/${table.id}`, { ...table, status: newStatus });
      toast.success('Status atualizado!');
      loadData();
    } catch (err) {
      toast.error(t('errors.generic'));
    }
  };

  // Count tables by status
  const statusCounts = tables.reduce((acc, t) => {
    acc[t.status] = (acc[t.status] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="tables-page" data-testid="tables-page">
      <div className="tables-header">
        <div className="tables-header-title">
          <h1>
            <Table2 className="h-6 w-6" />
            {t('tables.title')}
          </h1>
          <p>{tables.length} mesas cadastradas</p>
        </div>
        <Button 
          onClick={() => { setEditingTable(null); setModalOpen(true); }}
          data-testid="new-table-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('tables.newTable')}
        </Button>
      </div>

      {/* Status Summary */}
      <div className="tables-summary">
        <div className="tables-summary-item tables-summary-available">
          <span className="text-2xl font-bold">{statusCounts.disponivel || 0}</span>
          <span className="text-sm">{t('tables.available')}</span>
        </div>
        <div className="tables-summary-item tables-summary-occupied">
          <span className="text-2xl font-bold">{statusCounts.ocupada || 0}</span>
          <span className="text-sm">{t('tables.occupied')}</span>
        </div>
        <div className="tables-summary-item tables-summary-reserved">
          <span className="text-2xl font-bold">{statusCounts.reservada || 0}</span>
          <span className="text-sm">{t('tables.reserved')}</span>
        </div>
      </div>

      <div className="tables-grid">
        {tables.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center text-muted-foreground">
              {t('common.noResults')}
            </CardContent>
          </Card>
        ) : (
          tables.map(table => {
            const config = statusConfig[table.status] || statusConfig.disponivel;
            return (
              <Card 
                key={table.id} 
                className={`table-card ${config.class}`}
                data-testid={`table-card-${table.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="table-number">
                      <Table2 className="h-5 w-5" />
                      <span>{table.number}</span>
                    </div>
                    <Badge variant={config.variant}>
                      {config.label}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center gap-1 text-sm text-muted-foreground mb-3">
                    <Users className="h-4 w-4" />
                    <span>{table.capacity} pessoas</span>
                  </div>

                  <div className="flex gap-1">
                    {table.status === 'disponivel' && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleStatusChange(table, 'ocupada')}
                      >
                        Ocupar
                      </Button>
                    )}
                    {table.status === 'ocupada' && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleStatusChange(table, 'disponivel')}
                      >
                        Liberar
                      </Button>
                    )}
                    {table.status === 'reservada' && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleStatusChange(table, 'ocupada')}
                      >
                        Ocupar
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => {
                        setEditingTable(table);
                        setModalOpen(true);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setDeleteId(table.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      <TableModal
        open={modalOpen}
        onOpenChange={handleModalOpenChange}
        table={editingTable}
        onSave={handleSave}
        loading={saving}
      />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.confirm')}</AlertDialogTitle>
            <AlertDialogDescription>{t('tables.deleteConfirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
